import { Analysis } from '../types';
import * as db from './snapshotHistoryDB';

/**
 * Сервис для управления снимками результата анализа
 * Хранит историю изменений в IndexedDB для возможности отката
 * Использует кэш в памяти для синхронного доступа к текущей истории
 */

interface Snapshot {
  id: string;
  timestamp: number;
  analysis: Analysis;
  description: string;
}

interface SnapshotHistory {
  currentResultId: string;
  snapshots: Snapshot[];
  currentIndex: number;
}

const STORAGE_KEYS = {
  CURRENT_RESULT_ID: 'plinus_current_result_id'
} as const;

// Кэш в памяти для текущей истории (для синхронного доступа)
let memoryCache: SnapshotHistory | null = null;
let currentResultIdCache: string | null = null;

/**
 * Получает ID текущего результата из sessionStorage
 */
export function getCurrentResultId(): string | null {
  if (currentResultIdCache) {
    return currentResultIdCache;
  }
  return sessionStorage.getItem(STORAGE_KEYS.CURRENT_RESULT_ID);
}

/**
 * Устанавливает ID текущего результата в sessionStorage
 */
export function setCurrentResultId(resultId: string): void {
  currentResultIdCache = resultId;
  sessionStorage.setItem(STORAGE_KEYS.CURRENT_RESULT_ID, resultId);
}

/**
 * Получает историю снимков из кэша памяти
 */
export function getSnapshotHistory(): SnapshotHistory | null {
  return memoryCache;
}

/**
 * Сохраняет историю снимков (синхронно обновляет кэш, асинхронно сохраняет в IndexedDB)
 */
export function saveSnapshotHistory(history: SnapshotHistory): void {
  memoryCache = history;
  const resultId = history.currentResultId || getCurrentResultId();
  if (resultId) {
    // Сохраняем асинхронно в фоне
    db.safeSaveSnapshotHistory(resultId, history).catch(error => {
      console.error('Ошибка при сохранении истории в IndexedDB:', error);
    });
  }
}

/**
 * Инициализирует историю снимков для нового результата
 */
export async function initializeSnapshotHistory(resultId: string, initialAnalysis: Analysis): Promise<void> {
  const snapshot: Snapshot = {
    id: `snapshot_${Date.now()}`,
    timestamp: Date.now(),
    analysis: JSON.parse(JSON.stringify(initialAnalysis)), // Глубокое копирование
    description: 'Начальное состояние'
  };

  const history: SnapshotHistory = {
    currentResultId: resultId,
    snapshots: [snapshot],
    currentIndex: 0
  };

  setCurrentResultId(resultId);
  memoryCache = history;
  await db.safeSaveSnapshotHistory(resultId, history);
}

/**
 * Создает новый снимок и добавляет его в историю
 * Возвращает информацию о возможности отмены/возврата
 */
export function createSnapshot(analysis: Analysis, description: string, resultId?: string): { canUndo: boolean; canRedo: boolean } | null {
  let currentResultId = getCurrentResultId();
  
  // Если currentResultId не установлен, но передан resultId, используем его
  if (!currentResultId && resultId) {
    currentResultId = resultId;
    setCurrentResultId(resultId);
  }
  
  // Если все еще нет currentResultId, создаем новый на основе external_id
  if (!currentResultId && analysis.external_id) {
    currentResultId = `temp_${analysis.external_id}`;
    setCurrentResultId(currentResultId);
  }
  
  if (!currentResultId) {
    return null;
  }

  let history = getSnapshotHistory();
  
  // Если истории нет или она для другого результата, инициализируем новую
  if (!history || history.currentResultId !== currentResultId) {
    // Создаем начальный снимок синхронно в кэше
    const snapshot: Snapshot = {
      id: `snapshot_${Date.now()}`,
      timestamp: Date.now(),
      analysis: JSON.parse(JSON.stringify(analysis)),
      description: 'Начальное состояние'
    };

    history = {
      currentResultId: currentResultId,
      snapshots: [snapshot],
      currentIndex: 0
    };
    
    memoryCache = history;
    
    // Сохраняем в IndexedDB асинхронно в фоне
    db.safeSaveSnapshotHistory(currentResultId, history).catch(error => {
      console.error('Ошибка при сохранении истории в IndexedDB:', error);
    });
  }

  // Создаем новый снимок
  const newSnapshot: Snapshot = {
    id: `snapshot_${Date.now()}`,
    timestamp: Date.now(),
    analysis: JSON.parse(JSON.stringify(analysis)), // Глубокое копирование
    description
  };

  // Удаляем все снимки после текущего индекса (если пользователь откатился назад и сделал новое изменение)
  history.snapshots = history.snapshots.slice(0, history.currentIndex + 1);
  
  // Добавляем новый снимок
  history.snapshots.push(newSnapshot);
  history.currentIndex = history.snapshots.length - 1;

  // Ограничиваем количество снимков (например, максимум 50)
  const MAX_SNAPSHOTS = 50;
  if (history.snapshots.length > MAX_SNAPSHOTS) {
    // Сохраняем текущий индекс относительно конца массива
    const currentIndexFromEnd = history.snapshots.length - 1 - history.currentIndex;
    
    // Удаляем старые снимки, сохраняя последние MAX_SNAPSHOTS
    history.snapshots = history.snapshots.slice(-MAX_SNAPSHOTS);
    
    // Корректируем индекс с учетом удаленных снимков
    history.currentIndex = Math.max(0, history.snapshots.length - 1 - currentIndexFromEnd);
  }

  saveSnapshotHistory(history);
  
  // Вычисляем возможность отмены/возврата
  const canUndoResult = history.snapshots.length > 1 && history.currentIndex > 0;
  const canRedoResult = history.currentIndex < history.snapshots.length - 1;
  
  return { canUndo: canUndoResult, canRedo: canRedoResult };
}

/**
 * Проверяет, можно ли откатиться назад
 */
export function canUndo(): boolean {
  const history = getSnapshotHistory();
  if (!history) {
    return false;
  }
  
  // Проверяем, что есть хотя бы 2 снимка (начальный + хотя бы одно изменение)
  return history.snapshots.length > 1 && history.currentIndex > 0;
}

/**
 * Проверяет, можно ли вернуться вперед
 */
export function canRedo(): boolean {
  const history = getSnapshotHistory();
  return history ? history.currentIndex < history.snapshots.length - 1 : false;
}

/**
 * Откатывается к предыдущему снимку
 */
export function undo(): Analysis | null {
  const history = getSnapshotHistory();
  if (!history || history.currentIndex <= 0) {
    return null;
  }

  history.currentIndex--;
  saveSnapshotHistory(history);
  
  return history.snapshots[history.currentIndex].analysis;
}

/**
 * Возвращается к следующему снимку
 */
export function redo(): Analysis | null {
  const history = getSnapshotHistory();
  if (!history || history.currentIndex >= history.snapshots.length - 1) {
    return null;
  }

  history.currentIndex++;
  saveSnapshotHistory(history);
  
  return history.snapshots[history.currentIndex].analysis;
}

/**
 * Получает текущий снимок
 */
export function getCurrentSnapshot(): Analysis | null {
  const history = getSnapshotHistory();
  if (!history || history.snapshots.length === 0) {
    return null;
  }

  return history.snapshots[history.currentIndex].analysis;
}

/**
 * Получает текущий снимок для конкретного результата (асинхронно из IndexedDB)
 */
export async function getCurrentSnapshotForResult(resultId: string): Promise<Analysis | null> {
  const history = await db.getSnapshotHistory(resultId);
  if (!history || history.snapshots.length === 0) {
    return null;
  }

  return history.snapshots[history.currentIndex].analysis;
}

/**
 * Получает описание текущего снимка
 */
export function getCurrentSnapshotDescription(): string {
  const history = getSnapshotHistory();
  if (!history || history.snapshots.length === 0) {
    return '';
  }

  return history.snapshots[history.currentIndex].description;
}

/**
 * Получает описание предыдущего снимка (для кнопки отката)
 */
export function getUndoDescription(): string {
  const history = getSnapshotHistory();
  if (!history || history.currentIndex <= 0) {
    return '';
  }

  return history.snapshots[history.currentIndex - 1].description;
}

/**
 * Получает описание следующего снимка (для кнопки возврата)
 */
export function getRedoDescription(): string {
  const history = getSnapshotHistory();
  if (!history || history.currentIndex >= history.snapshots.length - 1) {
    return '';
  }

  return history.snapshots[history.currentIndex + 1].description;
}

/**
 * Очищает историю снимков для текущего результата
 */
export function clearSnapshotHistory(): void {
  memoryCache = null;
  sessionStorage.removeItem(STORAGE_KEYS.CURRENT_RESULT_ID);
  currentResultIdCache = null;
}

/**
 * Проверяет, соответствует ли текущий результат ID в истории снимков
 */
export async function isCurrentResultInHistory(resultId: string): Promise<boolean> {
  // Сначала проверяем текущий результат
  const currentResultId = getCurrentResultId();
  if (currentResultId === resultId && memoryCache) {
    return true;
  }
  
  // Затем проверяем, есть ли сохраненная история для этого результата в IndexedDB
  const savedHistory = await db.getSnapshotHistory(resultId);
  return savedHistory !== null;
}

/**
 * Сохраняет историю снимков для конкретного результата
 */
export async function saveResultSnapshotHistory(resultId: string, history: SnapshotHistory): Promise<void> {
  await db.safeSaveSnapshotHistory(resultId, history);
}

/**
 * Получает историю снимков для конкретного результата (асинхронно из IndexedDB)
 */
export async function getResultSnapshotHistory(resultId: string): Promise<SnapshotHistory | null> {
  return await db.getSnapshotHistory(resultId);
}

/**
 * Очищает историю снимков для конкретного результата
 */
export async function clearResultSnapshotHistory(resultId: string): Promise<void> {
  await db.deleteSnapshotHistory(resultId);
  // Если это текущая история в кэше, очищаем кэш
  if (memoryCache && memoryCache.currentResultId === resultId) {
    memoryCache = null;
  }
}

/**
 * Получает список всех результатов с сохраненной историей
 */
export async function getAllResultsWithHistory(): Promise<string[]> {
  return await db.getAllResultIds();
}

/**
 * Получает информацию о времени последнего доступа к истории результата
 */
export async function getResultHistoryTimestamp(resultId: string): Promise<number> {
  return await db.getResultHistoryTimestamp(resultId);
}

/**
 * Загружает историю из IndexedDB в кэш памяти
 */
export async function loadSnapshotHistoryIntoCache(resultId: string): Promise<boolean> {
  const history = await db.getSnapshotHistory(resultId);
  if (history) {
    memoryCache = history;
    currentResultIdCache = resultId;
    setCurrentResultId(resultId);
    return true;
  }
  return false;
}
